import React, { useState } from 'react';
import { Calculator, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { calculateLoanRepayment, comparePaymentFrequencies, formatCurrency, formatCurrencyDecimal } from '../utils/calculators';
import { captureCalculatorLead } from '../data/mock';

export const LoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState('500000');
  const [interestRate, setInterestRate] = useState('6.0');
  const [loanTerm, setLoanTerm] = useState('30');
  const [paymentFrequency, setPaymentFrequency] = useState('monthly');
  const [repaymentType, setRepaymentType] = useState('principalInterest');
  const [propertyType, setPropertyType] = useState('ownerOccupied');
  const [weeklyRent, setWeeklyRent] = useState('550');
  const [results, setResults] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);

  const handleCalculate = async () => {
    const result = calculateLoanRepayment(loanAmount, interestRate, loanTerm, paymentFrequency, repaymentType);
    const comp = comparePaymentFrequencies(loanAmount, interestRate, loanTerm, repaymentType);
    setResults(result);
    setComparison(comp);

    // Calculate cash flow for investment properties
    let cashFlowData = null;
    if (propertyType === 'investment' && result) {
      const rent = parseFloat(weeklyRent) || 0;
      
      // Convert payment to weekly for comparison
      let weeklyPayment;
      if (paymentFrequency === 'monthly') {
        weeklyPayment = (parseFloat(result.payment) * 12) / 52;
      } else if (paymentFrequency === 'fortnightly') {
        weeklyPayment = parseFloat(result.payment) / 2;
      } else {
        weeklyPayment = parseFloat(result.payment);
      }

      const weeklyCashFlow = rent - weeklyPayment;
      const monthlyCashFlow = (weeklyCashFlow * 52) / 12;
      const annualCashFlow = weeklyCashFlow * 52;

      cashFlowData = {
        weeklyRent: rent,
        weeklyPayment: weeklyPayment,
        weeklyCashFlow: weeklyCashFlow,
        monthlyCashFlow: monthlyCashFlow,
        annualCashFlow: annualCashFlow,
        isPositive: weeklyCashFlow >= 0
      };
      setCashFlow(cashFlowData);
    } else {
      setCashFlow(null);
    }

    // Capture calculator lead
    try {
      const inputs = {
        loanAmount: parseFloat(loanAmount),
        interestRate: parseFloat(interestRate),
        loanTerm: parseInt(loanTerm),
        paymentFrequency,
        repaymentType,
        propertyType,
        ...(propertyType === 'investment' && { weeklyRent: parseFloat(weeklyRent) })
      };

      const resultsData = {
        payment: result.payment,
        totalRepayment: result.totalRepayment,
        totalInterest: result.totalInterest,
        ...(cashFlowData && { cashFlow: cashFlowData })
      };

      await captureCalculatorLead('loan', inputs, resultsData);
    } catch (error) {
      console.error('Error capturing calculator lead:', error);
    }
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      'weekly': 'Weekly',
      'fortnightly': 'Fortnightly',
      'monthly': 'Monthly'
    };
    return labels[freq] || freq;
  };

  const getInterestSaved = (comparison) => {
    if (!comparison || comparison.length === 0) return null;
    const monthly = comparison.find(c => c.frequency === 'monthly');
    const weekly = comparison.find(c => c.frequency === 'weekly');
    if (monthly && weekly) {
      return parseFloat(monthly.totalInterest) - parseFloat(weekly.totalInterest);
    }
    return null;
  };

  return (
    <div className="w-full">
      <div className="grid md:grid-cols-2 gap-8">
        <Card className="border-2 border-[#7A9B76]/20 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#7A9B76]/5 to-transparent">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Calculator className="w-6 h-6 text-[#7A9B76]" />
              Loan Calculator
            </CardTitle>
            <CardDescription>Calculate your loan repayments and compare payment frequencies</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div className="space-y-2">
              <Label htmlFor="loanAmount">Loan Amount</Label>
              <Input
                id="loanAmount"
                type="number"
                value={loanAmount}
                onChange={(e) => setLoanAmount(e.target.value)}
                placeholder="500000"
                className="text-lg"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="interestRate">Interest Rate (%)</Label>
              <Input
                id="interestRate"
                type="number"
                step="0.1"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                placeholder="6.0"
                className="text-lg"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="loanTerm">Loan Term (Years)</Label>
              <Input
                id="loanTerm"
                type="number"
                value={loanTerm}
                onChange={(e) => setLoanTerm(e.target.value)}
                placeholder="30"
                className="text-lg"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="propertyType">Property Type</Label>
              <Select value={propertyType} onValueChange={(value) => {
                console.log('Property type changed to:', value);
                setPropertyType(value);
              }}>
                <SelectTrigger id="propertyType">
                  <SelectValue placeholder="Select property type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ownerOccupied">Owner Occupied</SelectItem>
                  <SelectItem value="investment">Investment Property</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {propertyType === 'investment' && (
              <div className="space-y-2 animate-in slide-in-from-top duration-300">
                <Label htmlFor="weeklyRent">Weekly Rent Received</Label>
                <Input
                  id="weeklyRent"
                  type="number"
                  value={weeklyRent}
                  onChange={(e) => setWeeklyRent(e.target.value)}
                  placeholder="550"
                  className="text-lg"
                />
                <p className="text-xs text-gray-500">Enter the weekly rent you receive from this investment property</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="repaymentType">Repayment Type</Label>
              <Select value={repaymentType} onValueChange={setRepaymentType}>
                <SelectTrigger id="repaymentType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="principalInterest">Principal & Interest</SelectItem>
                  <SelectItem value="interestOnly">Interest Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="paymentFrequency">Payment Frequency</Label>
              <Select value={paymentFrequency} onValueChange={setPaymentFrequency}>
                <SelectTrigger id="paymentFrequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="fortnightly">Fortnightly</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button 
              onClick={handleCalculate} 
              className="w-full bg-[#7A9B76] hover:bg-[#6B8A67] text-white py-6 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Calculate Repayments
            </Button>

            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4 text-sm">
              <p className="text-[#8B7355] font-medium">💡 Pro Tip:</p>
              <p className="text-gray-700 mt-1">
                {propertyType === 'investment' 
                  ? 'Investment properties with positive cash flow can help pay for themselves while building equity and tax benefits!'
                  : 'Switching from monthly to weekly payments can save you thousands in interest and help you pay off your loan years earlier!'}
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {results && (
            <>
              <Card className="border-2 border-[#D4AF37]/30 shadow-xl bg-gradient-to-br from-white to-[#D4AF37]/5">
                <CardHeader className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent">
                  <CardTitle className="text-2xl text-[#8B7355]">Your Repayment Results</CardTitle>
                  <CardDescription>Based on your selected frequency</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-6">
                  <div className="bg-[#7A9B76]/10 rounded-lg p-6 border-2 border-[#7A9B76]/30">
                    <p className="text-sm text-gray-600 mb-2">{getFrequencyLabel(results.frequency)} Payment</p>
                    <p className="text-4xl font-bold text-[#7A9B76]">
                      {formatCurrencyDecimal(results.payment)}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">Total Repayment</p>
                      <p className="text-xl font-semibold text-gray-800">
                        {formatCurrency(results.totalRepayment)}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">Total Interest</p>
                      <p className="text-xl font-semibold text-red-600">
                        {formatCurrency(results.totalInterest)}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Loan Amount</p>
                    <p className="text-lg font-semibold text-gray-800">
                      {formatCurrency(loanAmount)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {cashFlow && propertyType === 'investment' && (
                <Card className="border-2 border-[#7A9B76]/30 shadow-xl bg-gradient-to-br from-white to-[#7A9B76]/5">
                  <CardHeader className="bg-gradient-to-r from-[#7A9B76]/10 to-transparent">
                    <CardTitle className="text-2xl text-[#7A9B76]">Investment Cash Flow</CardTitle>
                    <CardDescription>Rent vs Mortgage Payment Analysis</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-6">
                    <div className={`rounded-lg p-6 border-2 ${cashFlow.isPositive ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                      <p className="text-sm text-gray-600 mb-2">Weekly Cash Flow</p>
                      <p className={`text-4xl font-bold ${cashFlow.isPositive ? 'text-green-600' : 'text-orange-600'}`}>
                        {cashFlow.isPositive ? '+' : ''}{formatCurrencyDecimal(cashFlow.weeklyCashFlow)}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        {cashFlow.isPositive ? 'Positive cash flow!' : 'Out of pocket per week'}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Weekly Rent</p>
                        <p className="text-lg font-semibold text-green-600">
                          {formatCurrencyDecimal(cashFlow.weeklyRent)}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Weekly Payment</p>
                        <p className="text-lg font-semibold text-red-600">
                          {formatCurrencyDecimal(cashFlow.weeklyPayment)}
                        </p>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Monthly Cash Flow:</span>
                        <span className={`font-semibold ${cashFlow.isPositive ? 'text-green-600' : 'text-orange-600'}`}>
                          {cashFlow.isPositive ? '+' : ''}{formatCurrencyDecimal(cashFlow.monthlyCashFlow)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Annual Cash Flow:</span>
                        <span className={`font-semibold ${cashFlow.isPositive ? 'text-green-600' : 'text-orange-600'}`}>
                          {cashFlow.isPositive ? '+' : ''}{formatCurrency(cashFlow.annualCashFlow)}
                        </span>
                      </div>
                    </div>

                    {!cashFlow.isPositive && (
                      <div className="bg-[#D4AF37]/10 border-l-4 border-[#D4AF37] rounded-r-lg p-4">
                        <p className="text-sm text-gray-700">
                          <strong>Remember:</strong> Negative gearing means your rental losses reduce your taxable income, potentially saving you thousands in tax. Use the Negative Gearing Calculator to see your tax benefits!
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {comparison && comparison.length > 0 && (
            <Card className="border-2 border-[#7A9B76]/20 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-[#D4AF37]" />
                  Payment Frequency Comparison
                </CardTitle>
                <CardDescription>See how much you can save by changing payment frequency</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {comparison.map((comp) => (
                  <div 
                    key={comp.frequency} 
                    className={`p-4 rounded-lg border-2 transition-all ${
                      comp.frequency === paymentFrequency 
                        ? 'border-[#7A9B76] bg-[#7A9B76]/5' 
                        : 'border-gray-200 bg-white hover:border-[#7A9B76]/50'
                    }`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <p className="font-semibold text-gray-800 capitalize">{comp.frequency}</p>
                      <p className="text-lg font-bold text-[#7A9B76]">
                        {formatCurrencyDecimal(comp.payment)}
                      </p>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Total Interest:</span>
                      <span className="font-semibold">{formatCurrency(comp.totalInterest)}</span>
                    </div>
                  </div>
                ))}
                
                {getInterestSaved(comparison) > 0 && (
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mt-4">
                    <p className="text-sm font-semibold text-green-800 mb-1">💰 Potential Savings</p>
                    <p className="text-lg font-bold text-green-700">
                      Save {formatCurrency(getInterestSaved(comparison))} by switching from monthly to weekly payments!
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
